/**
 * ResultModal — shown when the game ends (win or lose).
 * Shows result, emoji grid, and share button.
 */
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { hideResultModal } from '../../store/slices/appSlice';
import { resetGame, startClassicGame } from '../../store/slices/gameSlice';
import { resetKeyboard } from '../../store/slices/keyboardSlice';
import { GAME_STATUS, GAME_MODE } from '../../utils/constants';
import { generateShareText } from '../../utils/gameLogic';

const ResultModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const show = useSelector((state) => state.app.showResultModal);
  const { status, guesses, evaluations, targetWord, mode, gameCode } =
    useSelector((state) => state.game);
  const [copied, setCopied] = useState(false);

  if (!show) return null;

  const won = status === GAME_STATUS.WON;
  const shareText = generateShareText(guesses, evaluations, mode, won);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = shareText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNewGame = () => {
    dispatch(hideResultModal());
    dispatch(resetGame());
    dispatch(resetKeyboard());
    if (mode === GAME_MODE.CLASSIC) {
      dispatch(startClassicGame({ isRandom: true }));
    } else {
      navigate('/');
    }
  };

  const handleBackToHome = () => {
    dispatch(hideResultModal());
    dispatch(resetGame());
    dispatch(resetKeyboard());
    navigate('/');
  };

  const handleShareLink = async () => {
    if (mode === GAME_MODE.CUSTOM && gameCode) {
      const url = `${window.location.origin}/play/${gameCode}`;
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Fallback
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 sm:p-8 mx-4 max-w-sm w-full text-center">
        {/* Title */}
        <h2 className="text-2xl font-bold mb-2">
          {won ? '🎉 Tebrikler!' : '😞 Kaybettiniz'}
        </h2>

        {/* Subtitle */}
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          {won
            ? `${guesses.length}. denemede bildiniz!`
            : `Doğru cevap: ${targetWord}`}
        </p>

        {/* Emoji Grid Preview */}
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-5 font-mono text-sm whitespace-pre-wrap">
          {shareText}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleShare}
            className="w-full py-3 rounded-lg bg-correct text-white font-bold text-lg hover:opacity-90 transition-opacity"
          >
            {copied ? '✓ Kopyalandı!' : 'Sonucu Paylaş 📋'}
          </button>

          {mode === GAME_MODE.CUSTOM && gameCode && (
            <button
              onClick={handleShareLink}
              className="w-full py-3 rounded-lg bg-present text-white font-bold hover:opacity-90 transition-opacity"
            >
              Oyun Linkini Kopyala 🔗
            </button>
          )}

          <button
            onClick={handleNewGame}
            className="w-full py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 font-bold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {mode === GAME_MODE.CLASSIC ? 'Rastgele Yeni Oyun' : 'Yeni Oyun'}
          </button>

          <button
            onClick={handleBackToHome}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            Ana Menüye Dön
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultModal;
